import { useEffect, useState } from 'react';
import { apiClient } from '@/api/client';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { Plus, ArrowRight } from 'lucide-react';
import { useScope, brandColorVar } from '@/context/ScopeContext';
import TESTIDS from '@/constants/testIds';
import { toast } from 'sonner';

export default function Brands() {
    const { brands, accounts, refresh } = useScope();
    const [open, setOpen] = useState(false);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [busy, setBusy] = useState(false);

    const create = async () => {
        if (!name.trim()) return;
        setBusy(true);
        try {
            await apiClient.post('/brands', {
                name: name.trim(),
                description: description.trim(),
            });
            await refresh();
            setName('');
            setDescription('');
            setOpen(false);
            toast.success('Brand created');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to create brand');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold tracking-tight">Brands</h1>
                    <p className="text-sm text-muted-foreground">
                        Each brand owns its own voice, banned words, and connected social accounts.
                    </p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid={TESTIDS.brandsCreateButton}>
                            <Plus className="mr-1 h-4 w-4" /> New brand
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Create a brand</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-3">
                            <div>
                                <Label htmlFor="new-brand-name">Name</Label>
                                <Input
                                    id="new-brand-name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    data-testid={TESTIDS.brandsCreateNameInput}
                                />
                            </div>
                            <div>
                                <Label htmlFor="new-brand-desc">Description</Label>
                                <Textarea
                                    id="new-brand-desc"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    data-testid={TESTIDS.brandsCreateDescriptionInput}
                                    rows={3}
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button
                                onClick={create}
                                disabled={busy || !name.trim()}
                                data-testid={TESTIDS.brandsCreateSubmit}
                            >
                                {busy ? 'Creating…' : 'Create brand'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                {brands.map((b) => {
                    const accs = accounts.filter((a) => a.brand_id === b.id);
                    const accent = brandColorVar(b.color_slot);
                    return (
                        <Card
                            key={b.id}
                            className="flex flex-col border-border bg-card p-4 transition-colors duration-150 hover:border-[hsl(var(--brand-accent-current)/0.45)]"
                            style={{ '--brand-accent-current': accent }}
                            data-testid={TESTIDS.brandCard}
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <div className="flex items-center gap-2">
                                        <span
                                            className="inline-block h-2 w-2 rounded-full"
                                            style={{ backgroundColor: `hsl(${accent})` }}
                                        />
                                        <h3 className="text-base font-semibold">{b.name}</h3>
                                    </div>
                                    <div className="font-mono text-[11px] text-muted-foreground">/{b.slug}</div>
                                </div>
                                <div className="text-right text-[11px] text-muted-foreground">
                                    <div>{accs.length} accounts</div>
                                    <div>{(b.tone_keywords || []).length} tones</div>
                                </div>
                            </div>
                            <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{b.description}</p>
                            <div className="mt-3 flex flex-wrap gap-1">
                                {(b.tone_keywords || []).slice(0, 4).map((t) => (
                                    <span key={t} className="rounded-md border border-border bg-[hsl(var(--surface-2))] px-1.5 py-0.5 text-[11px] text-muted-foreground">
                                        {t}
                                    </span>
                                ))}
                            </div>
                            <div className="mt-auto flex items-center justify-between pt-4">
                                <div className="flex flex-wrap gap-1">
                                    {accs.slice(0, 3).map((a) => (
                                        <span key={a.id} className="font-mono text-[11px] text-muted-foreground">@{a.handle}</span>
                                    ))}
                                </div>
                                <Link
                                    to={`/brands/${b.id}`}
                                    className="inline-flex items-center gap-1 text-xs text-[hsl(var(--link))] hover:text-[hsl(var(--link-hover))]"
                                >
                                    Open <ArrowRight className="h-3 w-3" />
                                </Link>
                            </div>
                        </Card>
                    );
                })}
            </div>
        </div>
    );
}
